import React, { useEffect, useState } from 'react';
import { defineMessages } from 'react-intl';
import CropImageField from '@components/common/form/CropImageField';
import { AppConstants, UserTypes } from '@constants';
import apiConfig from '@constants/apiConfig';
import { commonStatus, statusOptions } from '@constants/masterData';
import useBasicForm from '@hooks/useBasicForm';
import useFetch from '@hooks/useFetch';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, SelectField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { showErrorMessage,showSucsessMessage } from '@services/notifyService';
import { handleRemoveImage } from '@utils';
import { Card, Col, Form, Row } from 'antd';

const message = defineMessages({
    objectName: 'Shop Account',
});

const ShopAccountForm = (props) => {
    const translate = useTranslate();
    const { formId, actions, dataDetail, onSubmit, setIsChangedFormValues, branchs, isEditing } = props;
    const { execute: executeUpFile } = useFetch(apiConfig.file.upload);
    const [ imageUrl, setImageUrl ] = useState(null);
    const { form, mixinFuncs, onValuesChange } = useBasicForm({
        onSubmit,
        setIsChangedFormValues,
    });
    const statusValues = translate.formatKeys(statusOptions, ['label']);

    const uploadFile = (file) => {
        executeUpFile({
            data: {
                type: 'LOGO',
                file: file,
            },
            onCompleted: (response) => {
                if (response.result === true) {
                    setImageUrl(response.data.filePath);
                    setIsChangedFormValues(true);
                }
                showSucsessMessage(translate.formatMessage(commonMessage.uploadFileSuccess), translate);
            },
            onError: (error) => {
                showErrorMessage(translate.formatMessage(commonMessage.uploadFileFail), translate);
            },
        });
    };

    const handleSubmit = (values) => {
        return mixinFuncs.handleSubmit({
            ...values,
            logoPath: imageUrl,
            groupId: 16,
            kind: UserTypes.SHOP,
        });
    };

    useEffect(() => {
        form.setFieldsValue({
            ...dataDetail,
            username: dataDetail?.account?.username,
            fullName: dataDetail?.account?.fullName,
            email: dataDetail?.account?.email,
            status: dataDetail?.account?.status,
        });
        setImageUrl(dataDetail?.logoPath);
    }, [ dataDetail ]);
    return (
        <BaseForm id={formId} onFinish={handleSubmit} form={form} onValuesChange={onValuesChange}>
            <Card className="card-form" bordered={false}>
                <Row gutter={16}>
                    <Col span={12}>
                        <CropImageField
                            label={translate.formatMessage(commonMessage.logoPath)}
                            name="logoPath"
                            imageUrl={imageUrl && `${AppConstants.contentRootUrl}${imageUrl}`}
                            aspect={1 / 1}
                            required={true}
                            uploadFile={uploadFile}
                            handleRemoveImage={() => handleRemoveImage(setImageUrl, form)}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.username)}
                            name="username"
                            placeholder={translate.formatMessage(commonMessage.placeholderUsername)}
                            disabled={isEditing}
                            autoComplete="off"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.fullName)}
                            name="fullName"
                            placeholder={translate.formatMessage(commonMessage.placeholderFullName)}
                        />
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            label={translate.formatMessage(commonMessage.password)}
                            required={!isEditing}
                            name="password"
                            type="password"
                            placeholder={translate.formatMessage(commonMessage.placeholderPassword)}
                            autoComplete="new-password"
                            rules={[
                                {
                                    validator: async () => {
                                        const isTouched = form.isFieldTouched('password');
                                        if (isTouched) {
                                            const value = form.getFieldValue('password');
                                            if (value.length < 6) {
                                                throw new Error(
                                                    translate.formatMessage(commonMessage.validatePassword),
                                                );
                                            }
                                        }
                                    },
                                },
                            ]}
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            label={translate.formatMessage(commonMessage.confirmPassword)}
                            required={!isEditing}
                            name="confirmPassword"
                            type="password"
                            placeholder={translate.formatMessage(commonMessage.placeholderConfirmPassword)}
                            autoComplete="new-password"
                            rules={[
                                {
                                    validator: async () => {
                                        const password = form.getFieldValue('password');
                                        const confirmPassword = form.getFieldValue('confirmPassword');
                                        if (password !== confirmPassword) {
                                            throw new Error(translate.formatMessage(commonMessage.passwordNotMatch));
                                        }
                                    },
                                },
                            ]}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <TextField label={translate.formatMessage(commonMessage.email)} name="email" type="email" autoComplete="off" rules={[{ type: 'email', message: translate.formatMessage(commonMessage.invalidEmail) }]} />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.phone)}
                            name="phone"
                            placeholder={translate.formatMessage(commonMessage.placeholderPhone)}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.zipCode)}
                            name="zipCode"
                            placeholder={translate.formatMessage(commonMessage.placeholderZipCode)}
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.city)}
                            name="city"
                            placeholder={translate.formatMessage(commonMessage.placeholderCity)}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.address)}
                            name="address"
                            placeholder={translate.formatMessage(commonMessage.placeholderAddress)}
                            type="textarea"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.taxNumber)}
                            name="taxNumber"
                            placeholder={translate.formatMessage(commonMessage.placeholderTaxNumber)}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <SelectField
                            label={translate.formatMessage(commonMessage.status)}
                            name="status"
                            required
                            options={statusValues}
                            initialValue={statusValues[0].value}
                        />
                    </Col>
                    <Col span={12}>
                        <Form.Item name="groupId" hidden initialValue={1}>
                            <input type="hidden" />
                        </Form.Item>
                    </Col>
                </Row>
                <div className="footer-card-form">{actions}</div>
            </Card>
        </BaseForm>
    );
};

export default ShopAccountForm;
